/**
 * Checks that a value is a plain object: not null and not an array.
 * @param value - The untrusted value.
 * @returns True when the value is a record of string keys.
 */
export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * Checks that a value is an array of strings.
 * @param value - The untrusted value.
 * @returns True when every element is a string.
 */
export function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every(v => typeof v === 'string')
}

const DECIMAL_PKID = /^[1-9][0-9]*$/

/**
 * Reads a pkid as a decimal string.
 * @param value - A decimal string, or a positive safe integer.
 * @returns The decimal string, or null when the value is not a pkid.
 */
export function decimalPkid(value: unknown): string | null {
  if (typeof value === 'string') return DECIMAL_PKID.test(value) ? value : null
  if (typeof value === 'number' && Number.isSafeInteger(value) && value > 0) {
    return String(value)
  }
  return null
}

/**
 * Reads a JSON integer, or null.
 * @param value - An untrusted value.
 * @returns The integer, null, or undefined when the value is neither.
 */
export function integerOrNull(value: unknown): number | null | undefined {
  if (value === null) return null
  return typeof value === 'number' && Number.isInteger(value)
    ? value
    : undefined
}

/**
 * Reads a payment method pkid from a JSON value.
 * @param value - An untrusted value.
 * @returns The pkid, or null when the value is not a positive safe integer.
 */
export function paymentMethodPkid(value: unknown): number | null {
  if (typeof value === 'number' && Number.isSafeInteger(value) && value > 0) {
    return value
  }
  return null
}
